import { Box, Text } from '@factorialco/deprecated-design-system'
import { AtsGetHiringPipelinePhasesDocument } from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { HiringStageBox } from 'modules/ats/components/HiringStages/HiringStageBox'
import { UNASSIGNED_STAGE, isHiringStageEditable } from 'modules/ats/lib/utils/hiringStages'
import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { EditableHiringStage } from './EditableHiringStage'
import { HiringPipelineSkeleton } from './HiringPipelineSkeleton'
import { UnassignedHiringPhases } from './UnassignedHiringPhases'
import { groupApplicationPhasesByHiringStage } from './utils'

interface HiringTeamProps {
  jobId: string
}

export const HiringPipeline = ({ jobId }: HiringTeamProps) => {
  const { data } = useQuery(AtsGetHiringPipelinePhasesDocument, {
    variables: { jobId },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccesHiringPipeline)
  }, [])

  const applicationPhases = data?.ats.applicationPhasesConnection.nodes || []
  const hiringStages = data?.ats.hiringStagesConnection.nodes || []

  const groupedStagesWithPhases = groupApplicationPhasesByHiringStage(
    hiringStages,
    applicationPhases
  )

  const hasData = hiringStages.length > 0

  return (
    <SectionComponentLayout>
      <Box gap="s48">
        <Box gap="s8">
          <Text size="400" color="grey1000" weight="semibold">
            {i18n.t('ats.job_posting_settings.hiring_pipeline.title')}
          </Text>
          <Text size="300" color="grey800">
            {i18n.t('ats.job_posting_settings.hiring_pipeline.description')}
          </Text>
          <HelpCenterLink name="about_the_hiring_pipeline">
            {i18n.t('common.learn_more')}
          </HelpCenterLink>
        </Box>

        {hasData ? (
          <Box gap="s16">
            {groupedStagesWithPhases.map((stage) => (
              <Box key={`stage-item-${stage.id}`} gap="s16">
                {stage.name === UNASSIGNED_STAGE ? (
                  <UnassignedHiringPhases unassignedPhases={stage.applicationPhases} />
                ) : isHiringStageEditable(stage.name) ? (
                  <EditableHiringStage jobId={jobId} stage={stage} />
                ) : (
                  <HiringStageBox stageName={stage.name} stageLabel={stage.label} />
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <HiringPipelineSkeleton />
        )}
      </Box>
    </SectionComponentLayout>
  )
}
